import { useCallback, useEffect, useRef, useState } from "react";
import { COLOR_PICKER_V2_VIEWS } from "..";
import {
  ColorPicker as ColorPickerComponent,
  toColor,
  useColor,
} from "react-color-palette";
import { t } from "@/i18n-js/instance";
import { useIsPropertiesPanelRevampEnabled } from "@circle-react-shared/BuilderUI/Hooks/useIsPropertiesPanelRevamp";
import { Button } from "../../uikit/Button";
import { usePopoverPortalContext } from "../PopoverPortalContext";
import { ThemePicker } from "../ThemePicker";
import { isCssVariable } from "../ThemePicker/ThemeColors/utils";
import { usePickerContext } from "../pickerContext";
import { ColorModelSelector } from "./ColorMode";
import { AlphaInput } from "./ModelInputs/AlphaInput";
import { COLOR_MODE_COMPONENT_MAP } from "./constants";
import "./styles.scss";

export type ColorModel = "hex" | "rgb" | "hsv";

export const ColorPicker = ({
  shouldShowAlpha = true,
  shouldShowThemeColors = false,
}: {
  shouldShowAlpha?: boolean;
  shouldShowThemeColors?: boolean;
}) => {
  const {
    initialColor = "#000000",
    onColorChange,
    onAddCustomColor,
    setCurrentView,
    themeColors,
    shouldRenderThemeColors,
  } = usePickerContext();
  const isPropertiesPanelV2 = useIsPropertiesPanelRevampEnabled();

  const getEffectiveColor = useCallback(
    (colorValue: string) => {
      if (isCssVariable(colorValue) && themeColors) {
        return themeColors[colorValue] || "#000000";
      }
      return colorValue;
    },
    [themeColors],
  );

  const { onClose } = usePopoverPortalContext();
  const [color, setColor] = useColor("hex", getEffectiveColor(initialColor));
  const [model, setModel] = useState<ColorModel>("hex");
  const pendingColorRef = useRef(color);

  useEffect(() => {
    const effectiveColor = getEffectiveColor(initialColor);
    setColor(toColor("hex", effectiveColor));
  }, [initialColor, setColor, getEffectiveColor]);

  const handleModelChange = (newModel: ColorModel) => {
    setModel(newModel);
  };

  const handleAlphaChange = (value: number) => {
    const alphaDecimal = value / 100;
    const newColor = {
      ...color,
      rgb: { ...color.rgb, a: alphaDecimal },
      hsv: { ...color.hsv, a: alphaDecimal },
      hex: color.hex,
    };
    setColor(newColor);
    pendingColorRef.current = newColor;
  };

  const ColorModeComponent = COLOR_MODE_COMPONENT_MAP[model];

  const handleSave = () => {
    onColorChange?.(pendingColorRef.current.hex);
    onAddCustomColor?.(pendingColorRef.current.hex);
    if (shouldRenderThemeColors) {
      setCurrentView(COLOR_PICKER_V2_VIEWS.THEME_COLORS);
    } else {
      onClose();
    }
  };

  if (isPropertiesPanelV2) {
    return (
      <div className="color-picker--v2-revamp">
        <ColorPickerComponent
          color={color}
          onChange={color => {
            setColor(color);
            pendingColorRef.current = color;
          }}
          width={264}
          height={157}
          hideHEX
          hideRGB
          hideHSV
        />

        <div className="flex flex-col gap-3">
          <div>
            {ColorModeComponent && (
              <ColorModeComponent
                color={color}
                setColor={newColor => {
                  setColor(newColor);
                  pendingColorRef.current = newColor;
                }}
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ColorModelSelector model={model} onChange={handleModelChange} />
            <AlphaInput
              value={
                color.rgb.a !== undefined ? Math.round(color.rgb.a * 100) : 100
              }
              onChange={handleAlphaChange}
            />
          </div>
        </div>

        {shouldShowThemeColors && <ThemePicker />}

        <div className="flex w-full justify-end">
          <Button variant="circle" type="button" onClick={handleSave}>
            {t("save")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="color-picker--v2 flex w-full flex-col items-center">
      <ColorPickerComponent
        color={color}
        onChange={color => {
          setColor(color);
          pendingColorRef.current = color;
        }}
        alpha={shouldShowAlpha}
        width={320}
        height={160}
        hideHEX
        hideRGB
        hideHSV
      />
      <div className="flex w-full items-center gap-2 px-4">
        <ColorModelSelector model={model} onChange={handleModelChange} />
        {ColorModeComponent && (
          <ColorModeComponent
            color={color}
            setColor={newColor => {
              setColor(newColor);
              pendingColorRef.current = newColor;
            }}
          />
        )}
      </div>
      <div className="mt-4 flex w-full justify-end px-4">
        <Button variant="circle" type="button" onClick={handleSave}>
          {t("save")}
        </Button>
      </div>
    </div>
  );
};
